import { useCallback, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { updateHouseholdAPI } from "../api/updateHouseholdAPI";
import {
  LIQUIDITY_NEEDS_OPTIONS,
  LIQUIDITY_TIME_HORIZON_OPTIONS,
  getLiquidityNeedsLabel,
  getLiquidityTimeHorizonLabel,
  parseLiquidityNeedsDisplay,
  parseLiquidityTimeHorizonDisplay
} from "../constants/accountConstants";
import {
  DECISION_MAKING_OPTIONS,
  PRIMARY_OBJECTIVE_OPTIONS,
  PRIMARY_USE_OPTIONS,
  RISK_TOLERANCE_OPTIONS,
  SOURCE_OF_FUNDS_OPTIONS,
  TAX_BRACKET_MAP,
  TAX_BRACKET_OPTIONS,
  TIME_HORIZON_OPTIONS,
  getAccountRiskTimeHorizonLabel,
  parseAccountRiskTimeHorizonDisplay
} from "../constants/householdConstants";
import { useToast } from "../context/ToastContext";
import {
  getBrokerageFlags,
  isFieldOptionalForIws,
  showAccountDecisionMaking,
  showAnnualExpenseRange,
  showExperienceField,
  showLiquidityRequirements,
  showSourceOfFunds,
  showSpecialExpenseRange,
  showUseOfFunds,
} from "../utils/brokerageFields";
import { humanizeError } from "../utils/userFriendlyError";
import { formatNumericInput } from "../utils/validationHelpers";

function MissingHouseholdFieldModal({
  isOpen,
  onClose,
  selectedField,
  household,
  missingFields,
  brokerageFlags: brokerageFlagsProp,
  onHouseholdUpdate,
  clientToken,
  household_id,
  onSaveAndNext,
}) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const brokerageFlags = brokerageFlagsProp ?? getBrokerageFlags(household);

  const handleFieldChange = useCallback((fieldPath, value) => {
    let sanitizedValue = value;
    
    // Basic number sanitization for simple fields
    if (
      [
        "annual_income",
        "estimated_total_net_worth",
        "estimated_liquid_net_worth",
        "annual_expense_range.start",
        "annual_expense_range.end",
        "special_expense_range.start",
        "special_expense_range.end",
      ].some((p) => fieldPath.startsWith(p) || p === fieldPath)
    ) {
      sanitizedValue = formatNumericInput(value);
    }

    setFormData(prev => {
      const newData = { ...prev };
      const keys = fieldPath.split('.');
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = sanitizedValue;
      return newData;
    });
  }, []);

  const validateRange = (fieldBase, label, fieldType) => {
    const startValue = getFormValue(`${fieldBase}.start`, "");
    const endValue = getFormValue(`${fieldBase}.end`, "");

    if (startValue !== "" && endValue !== "") {
      if (fieldType === 'start' && parseFloat(startValue) > parseFloat(endValue)) {
        toast.error(`Invalid range for ${label}: 'From' cannot exceed 'To'. The field has been reset.`);
        handleFieldChange(`${fieldBase}.start`, "");
      } else if (fieldType === 'end' && parseFloat(endValue) < parseFloat(startValue)) {
        toast.error(`Invalid range for ${label}: 'To' cannot be less than 'From'. The field has been reset.`);
        handleFieldChange(`${fieldBase}.end`, "");
      }
    }
  };

  const getFormValue = useCallback((fieldPath, fallback) => {
    if (!fieldPath) return fallback;
    const keys = fieldPath.split('.');
    let current = formData;
    for (const key of keys) {
      if (current && Object.prototype.hasOwnProperty.call(current, key)) {
        current = current[key];
      } else {
        return fallback;
      }
    }
    return current !== undefined ? current : fallback;
  }, [formData]);

  // Helper: Parse tax bracket display value to {start, end}
  const parseTaxBracket = (displayValue) => {
    if (!displayValue) return null;
    return TAX_BRACKET_MAP[displayValue] || null;
  };

  const handleSave = async () => {
    if (!household || !selectedField) return;

    setSaving(true);
    try {
      const updatePayload = {};
      const field = selectedField;

      // Handle income_details - process all missing income fields
      if (field === "income_details") {
        const missingIncomeFields = getMissingIncomeFields();

        missingIncomeFields.forEach((incomeField) => {
          // Handle tax_info / tax_percentage / tax_bracket
          if (incomeField === "tax_info" || incomeField === "tax_percentage" || incomeField === "tax_bracket") {
            const taxBracketDisplay = formData.tax_info?.tax_bracket_display;
            if (taxBracketDisplay) {
              const taxBracket = parseTaxBracket(taxBracketDisplay);
              if (taxBracket) {
                updatePayload.tax_info = { tax_bracket: taxBracket };
              }
            }
          }

          // Handle simple number fields
          if (
            incomeField === "annual_income" &&
            formData.annual_income !== undefined
          ) {
            updatePayload.annual_income = formData.annual_income
              ? parseFloat(formData.annual_income)
              : null;
          }
          if (
            incomeField === "estimated_total_net_worth" &&
            formData.estimated_total_net_worth !== undefined
          ) {
            updatePayload.estimated_total_net_worth =
              formData.estimated_total_net_worth
                ? parseFloat(formData.estimated_total_net_worth)
                : null;
          }
          if (
            incomeField === "estimated_liquid_net_worth" &&
            formData.estimated_liquid_net_worth !== undefined
          ) {
            updatePayload.estimated_liquid_net_worth =
              formData.estimated_liquid_net_worth
                ? parseFloat(formData.estimated_liquid_net_worth)
                : null;
          }

          // Handle range fields
          if (
            incomeField === "annual_expense_range" &&
            formData.annual_expense_range
          ) {
            const expenseRange = {
              start:
                formData.annual_expense_range.start !== undefined &&
                formData.annual_expense_range.start !== ""
                  ? parseFloat(formData.annual_expense_range.start)
                  : null,
              end:
                formData.annual_expense_range.end !== undefined &&
                formData.annual_expense_range.end !== ""
                  ? parseFloat(formData.annual_expense_range.end)
                  : null,
            };
            updatePayload.annual_expense_range = expenseRange;
          }

          if (
            incomeField === "special_expense_range" &&
            formData.special_expense_range
          ) {
            const specialExpenseRange = {
              start:
                formData.special_expense_range.start !== undefined &&
                formData.special_expense_range.start !== ""
                  ? parseFloat(formData.special_expense_range.start)
                  : null,
              end:
                formData.special_expense_range.end !== undefined &&
                formData.special_expense_range.end !== ""
                  ? parseFloat(formData.special_expense_range.end)
                  : null,
            };
            updatePayload.special_expense_range = specialExpenseRange;
          }
        });
      }

      // Handle tax_info (single field)
      if (field === "tax_info") {
        const taxBracketDisplay = formData.tax_info?.tax_bracket_display;
        if (taxBracketDisplay) {
          const taxBracket = parseTaxBracket(taxBracketDisplay);
          if (taxBracket) {
            updatePayload.tax_info = { tax_bracket: taxBracket };
          }
        }
      }

      // Handle simple number fields
      if (field === "annual_income" && formData.annual_income !== undefined) {
        updatePayload.annual_income = formData.annual_income
          ? parseFloat(formData.annual_income)
          : null;
      }
      if (
        field === "estimated_total_net_worth" &&
        formData.estimated_total_net_worth !== undefined
      ) {
        updatePayload.estimated_total_net_worth =
          formData.estimated_total_net_worth
            ? parseFloat(formData.estimated_total_net_worth)
            : null;
      }
      if (
        field === "estimated_liquid_net_worth" &&
        formData.estimated_liquid_net_worth !== undefined
      ) {
        updatePayload.estimated_liquid_net_worth =
          formData.estimated_liquid_net_worth
            ? parseFloat(formData.estimated_liquid_net_worth)
            : null;
      }

      // Handle range fields
      if (field === "annual_expense_range" && formData.annual_expense_range) {
        const expenseRange = {
          start:
            formData.annual_expense_range.start !== undefined &&
            formData.annual_expense_range.start !== ""
              ? parseFloat(formData.annual_expense_range.start)
              : null,
          end:
            formData.annual_expense_range.end !== undefined &&
            formData.annual_expense_range.end !== ""
              ? parseFloat(formData.annual_expense_range.end)
              : null,
        };
        updatePayload.annual_expense_range = expenseRange;
      }

      if (field === "special_expense_range" && formData.special_expense_range) {
        const specialExpenseRange = {
          start:
            formData.special_expense_range.start !== undefined &&
            formData.special_expense_range.start !== ""
              ? parseFloat(formData.special_expense_range.start)
              : null,
          end:
            formData.special_expense_range.end !== undefined &&
            formData.special_expense_range.end !== ""
              ? parseFloat(formData.special_expense_range.end)
              : null,
        };
        updatePayload.special_expense_range = specialExpenseRange;
      }

      // Handle investor_associations
      if (field === "investor_associations") {
        const associations = household.investor_associations || {};
        const fields = [
          "accredited_investor",
          "qualified_purchaser",
          "us_broker_dealer_association",
          "broker_dealer_employee",
          "ria_association",
          "shareholder_or_member_board_director",
          "low_priced_securities",
        ];

        const updatedAssociations = {};
        fields.forEach((key) => {
          const formValue = formData.investor_associations?.[key];
          if (formValue !== undefined) {
            updatedAssociations[key] = formValue;
          } else {
            updatedAssociations[key] =
              associations[key] === null || associations[key] === undefined
                ? false
                : associations[key];
          }
        });
        updatePayload.investor_associations = updatedAssociations;
      }

      // Handle investor_experience
      if (field === "investor_experience") {
        const experience = household.investor_experience || {};
        const fields = [
          "bonds",
          "stocks",
          "alternative_investments",
          "variable_annuities",
          "mutual_funds",
          "options",
          "partnerships",
        ];

        const updatedExperience = {};
        fields.forEach((key) => {
          const rawValue = formData.investor_experience?.[key];
          if (rawValue !== undefined && rawValue !== null && rawValue !== "") {
            const numValue = parseInt(rawValue, 10);
            updatedExperience[key] = isNaN(numValue)
              ? experience[key] ?? null
              : numValue;
          } else {
            updatedExperience[key] = experience[key] ?? null;
          }
        });
        updatePayload.investor_experience = updatedExperience;
      }

      // Handle investment_strategy_defaults
      if (field === "investment_strategy_defaults") {
        const strategy = household.investment_strategy_defaults || {};
        const missingStrategyFields = getMissingStrategyFields();

        const updatedStrategy = { ...strategy };

        missingStrategyFields.forEach((strategyField) => {
          if (strategyField === "invest_objectives") {
            const objectives = formData.investment_strategy_defaults?.invest_objectives;
            if (objectives) updatedStrategy.invest_objectives = objectives;
          } else if (strategyField === "risk_tolerance") {
            const risk = formData.investment_strategy_defaults?.risk_tolerance;
            if (risk) updatedStrategy.risk_tolerance = risk;
          } else if (strategyField === "time_horizon") {
            const horizon = formData.investment_strategy_defaults?.time_horizon;
            if (horizon) updatedStrategy.time_horizon = horizon;
          } else if (strategyField === "decision_making") {
            const dm = formData.investment_strategy_defaults?.decision_making;
            if (dm) updatedStrategy.decision_making = dm;
          } else if (strategyField === "source_of_funds") {
            const source = formData.investment_strategy_defaults?.source_of_funds;
            if (source) updatedStrategy.source_of_funds = source;
          } else if (strategyField === "use_of_funds") {
            const use = formData.investment_strategy_defaults?.use_of_funds;
            if (use) updatedStrategy.use_of_funds = use;
          } else if (strategyField === "liquidity_requirements") {
            const amount = formData.investment_strategy_defaults?.liquidity_requirements?.amount;
            const horizon = formData.investment_strategy_defaults?.liquidity_requirements?.time_horizon;
            
            if (!updatedStrategy.liquidity_requirements) {
              updatedStrategy.liquidity_requirements = {};
            }
            if (amount !== undefined) updatedStrategy.liquidity_requirements.amount = amount;
            if (horizon !== undefined) updatedStrategy.liquidity_requirements.time_horizon = horizon;
          }
        });

        updatePayload.investment_strategy_defaults = updatedStrategy;
      }

      // Only send if there are changes
      if (Object.keys(updatePayload).length > 0) {
        let success = false;
        if (clientToken && household_id) {
          const payload = { household: updatePayload };
          const result = await updateHouseholdAPI(
            household_id,
            clientToken,
            payload,
          );
          success = result.success;
          if (!success) {
            toast.error(result.error || "Failed to update household.");
          }
        } else {
          await axiosInstance.put(`/households/${household.id}`, updatePayload);
          success = true;
        }

        if (success) {
          if (onSaveAndNext) {
            onSaveAndNext();
          } else {
            onClose();
          }
          if (onHouseholdUpdate) {
            onHouseholdUpdate(null);
          }
          setFormData({});
        }
      } else if (onSaveAndNext) {
        onSaveAndNext();
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Error updating household:", error);
      toast.error(humanizeError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({});
    onClose();
  };

  if (!isOpen || !selectedField) return null;

  const getFieldTitle = () => {
    if (selectedField === 'investor_associations') return 'Qualifying Questions';
    if (selectedField === 'income_details') return 'Income Details';
    if (selectedField === 'investment_strategy_defaults') return 'Investment Strategy';
    return selectedField.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get income fields that are missing
  const getMissingIncomeFields = () => {
    if (!missingFields?.household) return [];
    const incomeFields = [
      'tax_info',
      'tax_percentage',
      'tax_bracket',
      'estimated_liquid_net_worth',
      'estimated_total_net_worth',
      'annual_income',
      'annual_expense_range',
      'special_expense_range'
    ];
    return missingFields.household.filter(field => {
      if (field === 'annual_expense_range' && !showAnnualExpenseRange(brokerageFlags)) return false;
      if (field === 'special_expense_range' && !showSpecialExpenseRange(brokerageFlags)) return false;
      if (
        (field === 'tax_info' || field === 'tax_bracket' || field === 'tax_percentage') &&
        isFieldOptionalForIws(brokerageFlags)
      ) return false;
      return incomeFields.includes(field);
    });
  };

  // Get strategy fields that are missing
  const getMissingStrategyFields = () => {
    if (!missingFields?.household) return [];
    
    const strategyFieldsList = [
      'invest_objectives',
      'risk_tolerance',
      'time_horizon',
      'decision_making',
      'source_of_funds',
      'use_of_funds',
      'liquidity_requirements'
    ];

    // Brokerage-conditional strategy fields: hide entirely when not required.
    const isStrategyFieldAllowed = (f) => {
      if (f === 'source_of_funds') return showSourceOfFunds(brokerageFlags);
      if (f === 'use_of_funds') return showUseOfFunds(brokerageFlags);
      if (f === 'liquidity_requirements') return showLiquidityRequirements(brokerageFlags);
      if (f === 'decision_making') return showAccountDecisionMaking(brokerageFlags);
      return true;
    };

    // If the top-level group is missing, return all sub-fields that are truly empty
    if (missingFields.household.includes('investment_strategy_defaults')) {
      const currentStrategy = household.investment_strategy_defaults || {};
      return strategyFieldsList.filter(f => {
        if (!isStrategyFieldAllowed(f)) return false;
        const val = currentStrategy[f];
        if (val === null || val === undefined) return true;
        if (Array.isArray(val) && val.length === 0) return true;
        if (typeof val === 'object' && Object.keys(val).length === 0) return true;
        return false;
      });
    }

    return missingFields.household.filter(field => strategyFieldsList.includes(field) && isStrategyFieldAllowed(field));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-900">
            {getFieldTitle()}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="px-6 py-4">
          {selectedField === 'income_details' && (
            <div className="space-y-4">
              {(() => {
                const missingIncomeFields = getMissingIncomeFields();
                return (
                  <>
                    {(missingIncomeFields.includes('tax_info') || 
                      missingIncomeFields.includes('tax_bracket') || 
                      missingIncomeFields.includes('tax_percentage')) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Tax Bracket (%)</label>
                        <select
                          value={getFormValue('tax_info.tax_bracket_display', '')}
                          onChange={(e) => handleFieldChange('tax_info.tax_bracket_display', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        >
                          <option value="">Select Tax Bracket</option>
                          {TAX_BRACKET_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Net Worths */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {missingIncomeFields.includes('estimated_liquid_net_worth') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Liquid Net Worth</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                            <input
                              type="number"
                              min={0}
                              placeholder="0"
                              value={getFormValue('estimated_liquid_net_worth', '')}
                              onChange={(e) => handleFieldChange('estimated_liquid_net_worth', e.target.value)}
                              onKeyDown={(e) => e.key === '-' && e.preventDefault()}
                              onWheel={(e) => e.currentTarget.blur()}
                              className="w-full px-3 py-2 pl-7 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </div>
                        </div>
                      )}

                      {missingIncomeFields.includes('estimated_total_net_worth') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Total Net Worth</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                            <input
                              type="number"
                              min={0}
                              placeholder="0"
                              value={getFormValue('estimated_total_net_worth', '')}
                              onChange={(e) => handleFieldChange('estimated_total_net_worth', e.target.value)}
                              onKeyDown={(e) => e.key === '-' && e.preventDefault()}
                              onWheel={(e) => e.currentTarget.blur()}
                              className="w-full px-3 py-2 pl-7 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Income */}
                    {missingIncomeFields.includes('annual_income') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Annual Household Income</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                          <input
                            type="number"
                            min={0}
                            placeholder="0"
                            value={getFormValue('annual_income', '')}
                            onChange={(e) => handleFieldChange('annual_income', e.target.value)}
                            onKeyDown={(e) => e.key === '-' && e.preventDefault()}
                            onWheel={(e) => e.currentTarget.blur()}
                            className="w-full px-3 py-2 pl-7 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                        </div>
                      </div>
                    )}

                    {/* Expenses */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {missingIncomeFields.includes('annual_expense_range') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Annual Expenses</label>
                          <div className="flex items-center gap-3">
                            <div className="relative flex-1">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
                              <input
                                type="number"
                                min={0}
                                placeholder="Min"
                                value={getFormValue('annual_expense_range.start', '')}
                                onChange={(e) => handleFieldChange('annual_expense_range.start', e.target.value)}
                                onBlur={() => validateRange('annual_expense_range', 'Annual Expenses')}
                                onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                onWheel={(e) => e.currentTarget.blur()}
                                className="w-full px-3 py-2 pl-7 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              />
                            </div>
                            <div className="text-gray-400 font-medium">–</div>
                            <div className="relative flex-1">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
                              <input
                                type="number"
                                min={0}
                                placeholder="Max"
                                value={getFormValue('annual_expense_range.end', '')}
                                onChange={(e) => handleFieldChange('annual_expense_range.end', e.target.value)}
                                onBlur={() => validateRange('annual_expense_range', 'Annual Expenses', 'end')}
                                onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                onWheel={(e) => e.currentTarget.blur()}
                                className="w-full px-3 py-2 pl-7 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {missingIncomeFields.includes('special_expense_range') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Special Expenses</label>
                          <div className="flex items-center gap-3">
                            <div className="relative flex-1">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
                              <input
                                type="number"
                                min={0}
                                placeholder="Min"
                                value={getFormValue('special_expense_range.start', '')}
                                onChange={(e) => handleFieldChange('special_expense_range.start', e.target.value)}
                                onBlur={() => validateRange('special_expense_range', 'Special Expenses', 'start')}
                                onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                onWheel={(e) => e.currentTarget.blur()}
                                className="w-full px-3 py-2 pl-7 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              />
                            </div>
                            <div className="text-gray-400 font-medium">–</div>
                            <div className="relative flex-1">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
                              <input
                                type="number"
                                min={0}
                                placeholder="Max"
                                value={getFormValue('special_expense_range.end', '')}
                                onChange={(e) => handleFieldChange('special_expense_range.end', e.target.value)}
                                onBlur={() => validateRange('special_expense_range', 'Special Expenses', 'end')}
                                onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                onWheel={(e) => e.currentTarget.blur()}
                                className="w-full px-3 py-2 pl-7 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {selectedField === 'tax_info' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tax Bracket (%)</label>
                <select
                  value={getFormValue('tax_info.tax_bracket_display', '')}
                  onChange={(e) => handleFieldChange('tax_info.tax_bracket_display', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                >
                  <option value="">Select Tax Bracket</option>
                  {TAX_BRACKET_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {(selectedField === 'estimated_liquid_net_worth' || 
            selectedField === 'estimated_total_net_worth' || 
            selectedField === 'annual_income') && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {selectedField === 'estimated_liquid_net_worth' ? 'Estimated Liquid Net Worth' :
                   selectedField === 'estimated_total_net_worth' ? 'Estimated Total Net Worth' :
                   'Annual Income'}
                </label>
                <input
                  type="number"
                  min={0}
                  placeholder="Enter amount"
                  value={getFormValue(selectedField, '')}
                  onChange={(e) => handleFieldChange(selectedField, e.target.value)}
                  onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            </div>
          )}

          {(selectedField === 'annual_expense_range' || 
            selectedField === 'special_expense_range') && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {selectedField === 'annual_expense_range' ? 'Annual Expenses Range' : 'Special Expenses Range'}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    placeholder="From"
                    value={getFormValue(`${selectedField}.start`, '')}
                    onChange={(e) => handleFieldChange(`${selectedField}.start`, e.target.value)}
                    onBlur={() => validateRange(selectedField, selectedField === 'annual_expense_range' ? 'Annual Expenses' : 'Special Expenses', 'start')}
                    onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="number"
                    min={0}
                    placeholder="To"
                    value={getFormValue(`${selectedField}.end`, '')}
                    onChange={(e) => handleFieldChange(`${selectedField}.end`, e.target.value)}
                    onBlur={() => validateRange(selectedField, selectedField === 'annual_expense_range' ? 'Annual Expenses' : 'Special Expenses', 'end')}
                    onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
              </div>
            </div>
          )}

          {selectedField === 'investor_associations' && (
            <div className="space-y-4">
              {(() => {
                const associations = household.investor_associations || {};
                const fields = [
                  { key: 'accredited_investor', label: 'Are you an accredited investor' },
                  { key: 'qualified_purchaser', label: 'Are you a qualified purchaser' },
                  { key: 'us_broker_dealer_association', label: 'Are you associated with a US Broker Dealer' },
                  { key: 'broker_dealer_employee', label: 'Are you an employee of a Broker Dealer' },
                  { key: 'ria_association', label: 'Are you associated with an RIA' },
                  { key: 'shareholder_or_member_board_director', label: 'Are you a shareholder or board member of a public company' },
                  { key: 'low_priced_securities', label: 'Does the household trade low-priced or restricted securities?' }
                ];
                
                // Only show fields that are null/undefined
                const nullFields = fields.filter(field => 
                  associations[field.key] === null || associations[field.key] === undefined
                );
                
                return nullFields.map((field) => (
                  <div key={field.key} className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium text-gray-700">{field.label}</span>
                    <button
                      type="button"
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                        getFormValue(`investor_associations.${field.key}`, false) ? 'bg-emerald-600' : 'bg-gray-200'
                      }`}
                      onClick={() => {
                        const currentValue = getFormValue(`investor_associations.${field.key}`, false);
                        handleFieldChange(`investor_associations.${field.key}`, !currentValue);
                      }}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        getFormValue(`investor_associations.${field.key}`, false) ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                ));
              })()}
            </div>
          )}

          {selectedField === 'investor_experience' && (
            <div className="space-y-4">
              {(() => {
                const experience = household.investor_experience || {};
                const fields = [
                  { key: 'bonds', label: 'Bonds' },
                  { key: 'stocks', label: 'Stocks' },
                  { key: 'alternative_investments', label: 'Alternative Investments' },
                  { key: 'variable_annuities', label: 'Variable Annuities' },
                  { key: 'mutual_funds', label: 'Mutual Funds' },
                  { key: 'options', label: 'Options' },
                  { key: 'partnerships', label: 'Partnerships' }
                ];
                
                // Only show fields that are null/undefined AND required for this brokerage
                const nullFields = fields.filter(field =>
                  (experience[field.key] === null || experience[field.key] === undefined) &&
                  showExperienceField(field.key, brokerageFlags, household?.accounts)
                );

                return (
                  <div className="grid grid-cols-2 gap-4">
                    {nullFields.map((field) => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{field.label} (Years)</label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          placeholder="Enter years"
                          value={getFormValue(`investor_experience.${field.key}`, '')}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            // Allow empty value, but clear if value exceeds 100
                            const numValue = value === '' ? '' : (parseInt(value, 10) > 100 ? '' : value);
                            handleFieldChange(`investor_experience.${field.key}`, numValue);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                            if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Enter'].includes(e.key) && !e.ctrlKey && !e.metaKey) {
                              e.preventDefault();
                            }
                          }}
                          onPaste={(e) => {
                            e.preventDefault();
                            const paste = (e.clipboardData || window.clipboardData).getData('text');
                            const numericValue = paste.replace(/[^0-9]/g, '');
                            const numValue = numericValue === '' ? '' : (parseInt(numericValue, 10) > 100 ? '' : numericValue);
                            handleFieldChange(`investor_experience.${field.key}`, numValue);
                          }}
                          onWheel={(e) => e.currentTarget.blur()}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {selectedField === 'investment_strategy_defaults' && (
            <div className="space-y-4">
              {(() => {
                const missingStrategyFields = getMissingStrategyFields();
                return (
                  <>
                    {missingStrategyFields.includes('invest_objectives') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Primary Investment Objective</label>
                        <select
                          value={getFormValue('investment_strategy_defaults.invest_objectives[0]', '')}
                          onChange={(e) => handleFieldChange('investment_strategy_defaults.invest_objectives', [e.target.value])}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        >
                          <option value="">Select Objective</option>
                          {PRIMARY_OBJECTIVE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      </div>
                    )}

                    {missingStrategyFields.includes('risk_tolerance') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Risk Tolerance</label>
                        <select
                          value={getFormValue('investment_strategy_defaults.risk_tolerance', '')}
                          onChange={(e) => handleFieldChange('investment_strategy_defaults.risk_tolerance', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        >
                          <option value="">Select Risk</option>
                          {RISK_TOLERANCE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      </div>
                    )}

                    {missingStrategyFields.includes('time_horizon') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Time Horizon</label>
                        <select
                          value={getAccountRiskTimeHorizonLabel(getFormValue('investment_strategy_defaults.time_horizon', null), '')}
                          onChange={(e) => handleFieldChange('investment_strategy_defaults.time_horizon', parseAccountRiskTimeHorizonDisplay(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        >
                          <option value="">Select Time Horizon</option>
                          {TIME_HORIZON_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      </div>
                    )}

                    {missingStrategyFields.includes('decision_making') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Account Decision Making</label>
                        <select
                          value={getFormValue('investment_strategy_defaults.decision_making', '')}
                          onChange={(e) => handleFieldChange('investment_strategy_defaults.decision_making', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        >
                          <option value="">Select Decision Making</option>
                          {DECISION_MAKING_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      </div>
                    )}

                    {missingStrategyFields.includes('source_of_funds') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Source of Funds</label>
                        <select
                          value={getFormValue('investment_strategy_defaults.source_of_funds[0]', '')}
                          onChange={(e) => handleFieldChange('investment_strategy_defaults.source_of_funds', [e.target.value])}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        >
                          <option value="">Select Source</option>
                          {SOURCE_OF_FUNDS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      </div>
                    )}

                    {missingStrategyFields.includes('use_of_funds') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Use of Funds</label>
                        <select
                          value={getFormValue('investment_strategy_defaults.use_of_funds', '')}
                          onChange={(e) => handleFieldChange('investment_strategy_defaults.use_of_funds', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        >
                          <option value="">Select Use</option>
                          {PRIMARY_USE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      </div>
                    )}

                    {missingStrategyFields.includes('liquidity_requirements') && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Liquidity Needed ($)</label>
                          <select
                            value={getLiquidityNeedsLabel(getFormValue('investment_strategy_defaults.liquidity_requirements.amount', null), '')}
                            onChange={(e) => handleFieldChange('investment_strategy_defaults.liquidity_requirements.amount', parseLiquidityNeedsDisplay(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          >
                            <option value="">Select Amount</option>
                            {LIQUIDITY_NEEDS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Liquidity Horizon (Years)</label>
                          <select
                            value={getLiquidityTimeHorizonLabel(getFormValue('investment_strategy_defaults.liquidity_requirements.time_horizon', null), '')}
                            onChange={(e) => handleFieldChange('investment_strategy_defaults.liquidity_requirements.time_horizon', parseLiquidityTimeHorizonDisplay(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          >
                            <option value="">Select Horizon</option>
                            {LIQUIDITY_TIME_HORIZON_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Save & Next
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MissingHouseholdFieldModal;
